"use client";

import { Search } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useTransition } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { provinciasEspana } from "@/lib/enums";
import { createClient } from "@/utils/supabase/client";
import { Label } from "./ui/label";
import { MultiSelect, MultiSelectOption } from "./ui/multiselect";
import { Select } from "./ui/select";

export default function FiltersBuscar() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const supabase = createClient();
  const [professional_specialties, setProfessionalSpecialties] = useState<
    MultiSelectOption[]
  >([]);
  const [filters, setFilters] = useState({
    q: searchParams.get("q") || "",
    province: searchParams.get("province") || "",
    city: searchParams.get("city") || "",
    specialty: searchParams.get("specialty")?.split(",") || [],
    minExperience: searchParams.get("minExperience") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    minRating: searchParams.get("minRating") || "",
    onlineOnly: searchParams.get("onlineOnly") === "true",
    verifiedOnly: searchParams.get("verifiedOnly") === "true",
    languages: searchParams.get("languages")?.split(",") || [],
  });

  useEffect(() => {
    const getSpecialties = async () => {
      const { data: specialties, error } = await supabase
        .from("professional_specialties")
        .select("id,name");
      if (error) {
        throw new Error("Error fetching specialties");
      }
      setProfessionalSpecialties(
        specialties.map((specialty: any) => {
          return {
            value: specialty.id.toString(),
            label: specialty.name,
          };
        })
      );
    };
    getSpecialties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createQueryString = (
    params: Record<string, string | string[] | boolean | null>
  ) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === "" || value === false) {
        newSearchParams.delete(key);
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          newSearchParams.set(key, value.join(","));
        } else {
          newSearchParams.delete(key);
        }
      } else {
        newSearchParams.set(key, String(value));
      }
    });

    return newSearchParams.toString();
  };

  const handleFilterChange = (
    key: string,
    value: string | string[] | boolean
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = (e: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    startTransition(() => {
      const queryString = createQueryString({
        ...filters,
      });
      router.push(`/buscar?${queryString}`);
    });
  };
  const handleReset = () => {
    setFilters({
      q: "",
      province: "",
      city: "",
      specialty: [""],
      minExperience: "",
      maxPrice: "",
      minRating: "",
      onlineOnly: false,
      verifiedOnly: false,
      languages: [],
    });

    startTransition(() => {
      router.push("/buscar");
    });
  };

  return (
    <div className="mb-8 bg-card-bg rounded-lg shadow px-4">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="filters">
          <AccordionTrigger className="text-lg hover:no-underline">
            Filtros de búsqueda
          </AccordionTrigger>
          <AccordionContent>
            <form onSubmit={handleSearch} className="space-y-6">
              <div className="relative">
                <Label htmlFor="search">Buscar por nombre u objetivo</Label>
                <Search className="absolute left-3 top-[1.9rem] h-5 w-5 text-primary" />

                <Input
                  type="text"
                  name="search"
                  id="search"
                  placeholder="Buscar por nombre u objetivo"
                  value={filters.q}
                  onChange={(e) => handleFilterChange("q", e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Ubicación */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="province">Provincia</Label>
                    <Select
                      id="province"
                      name="province"
                      value={filters.province}
                      placeholder="Selecciona tu provincia"
                      onChange={(value) =>
                        handleFilterChange("province", value)
                      }
                      options={[
                        {
                          value: "",
                          label: "Selecciona tu provincia",
                        },
                      ].concat(
                        provinciasEspana.map((provincia) => ({
                          value: provincia,
                          label: provincia,
                        }))
                      )}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Ciudad</Label>
                    <Input
                      type="text"
                      name="city"
                      id="city"
                      placeholder="Ciudad"
                      value={filters.city}
                      onChange={(e) =>
                        handleFilterChange("city", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Especialidad y Experiencia */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="specialties">Especialidad</Label>
                  <MultiSelect
                    options={professional_specialties}
                    id="specialties"
                    name="specialties"
                    value={filters.specialty as string[]}
                    placeholder="Selecciona tus especialidades"
                    onChange={(values: any) => {
                      handleFilterChange("specialty", values);
                    }}
                  />
                </div>

                <div>
                  <Label htmlFor="minExperience">Experiencia</Label>
                  <Select
                    id="minExperience"
                    name="minExperience"
                    value={filters.minExperience}
                    options={[
                      { value: "", label: "Experiencia mínima" },
                      { value: "1", label: "+1 años" },
                      { value: "2", label: "+2 años" },
                      { value: "5", label: "+4 años" },
                      { value: "10", label: "+6 años" },
                    ]}
                    onChange={(value) =>
                      handleFilterChange("minExperience", value)
                    }
                  />
                </div>
              </div>

              {/* Switches */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={filters.onlineOnly}
                    onCheckedChange={(checked) =>
                      handleFilterChange("onlineOnly", checked)
                    }
                  />
                  <label>Solo consultas online</label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={filters.verifiedOnly}
                    onCheckedChange={(checked) =>
                      handleFilterChange("verifiedOnly", checked)
                    }
                  />
                  <label>Solo profesionales verificados</label>
                </div>
              </div>

              {/* Valoración y Precio */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="minRating">Valoración</Label>
                  <Select
                    id="minRating"
                    name="minRating"
                    value={filters.minRating}
                    options={[
                      { value: "", label: "Valoración mínima" },
                      { value: "1", label: "1+" },
                      { value: "2", label: "2+" },
                      { value: "3", label: "3+" },
                      { value: "4", label: "4+" },
                      { value: "5", label: "5+" },
                    ]}
                    onChange={(value) => handleFilterChange("minRating", value)}
                  />
                </div>

                <div>
                  <Label htmlFor="maxPrice">Precio</Label>
                  <Select
                    id="maxPrice"
                    name="maxPrice"
                    value={filters.maxPrice}
                    options={[
                      { value: "", label: "Precio máximo" },
                      { value: "50", label: "20€" },
                      { value: "100", label: "40€" },
                      { value: "200", label: "60€" },
                      { value: "500", label: "80€" },
                      { value: "1000", label: "100€" },
                    ]}
                    onChange={(value) => handleFilterChange("maxPrice", value)}
                  />
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex justify-end space-x-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleReset}
                  disabled={isPending}
                >
                  Limpiar filtros
                </Button>
                <Button type="submit" disabled={isPending}>
                  Buscar
                </Button>
              </div>
            </form>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
